import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Check, Clock, AlertTriangle, Lock, EyeOff, Eye,
} from "lucide-react";
import { STEP_STATUS } from "@/data/mockData";

/**
 * Modal compacte de validation d'étape (Sprint V1 · Point 6).
 *
 * L'ancien drawer immense a été remplacé par une modale centrée haut de 480px
 * dont les 3 boutons (En cours, Bloquer, Terminer) sont *toujours* visibles
 * sans scroll, sur desktop comme mobile.
 */

const STATUS_META = {
  [STEP_STATUS.DONE]:        { label: "Terminé",  bg: "bg-[#DCFCE7]", text: "text-[#166534]", dot: "#22C55E" },
  [STEP_STATUS.IN_PROGRESS]: { label: "En cours", bg: "bg-[#C8FF2F]", text: "text-[#111111]", dot: "#111111" },
  [STEP_STATUS.BLOCKED]:     { label: "Bloqué",   bg: "bg-[#FEE2E2]", text: "text-[#991B1B]", dot: "#EF4444" },
  [STEP_STATUS.UPCOMING]:    { label: "À venir",  bg: "bg-[#F3F4F6]", text: "text-[#6B7280]", dot: "#9CA3AF" },
};

const StepDetailSheet = ({ step, onClose, onUpdate, readOnly = false, allSteps = [] }) => {
  const open = Boolean(step);
  const [saving, setSaving] = useState(false);
  const [showBlockedForm, setShowBlockedForm] = useState(false);
  const [blockingReason, setBlockingReason] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setShowBlockedForm(false);
    setBlockingReason(step?.blocking_reason || "");
    setError(null);
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEsc);
    };
  }, [open, step, onClose]);

  if (!open) return null;

  const sm = STATUS_META[step.status] || STATUS_META[STEP_STATUS.UPCOMING];
  const blocksUntil = step.blocks_until || [];
  const depSteps = blocksUntil
    .map((k) => allSteps.find((s) => s.key === k))
    .filter(Boolean);
  const unmetDeps = depSteps.filter((s) => s.status !== STEP_STATUS.DONE);
  const currentStatus = step.status;

  const doAction = async (newStatus, extra = {}) => {
    if (saving || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      await onUpdate?.({ status: newStatus, ...extra });
    } catch (e) {
      const payload = e?.data?.detail;
      if (payload && typeof payload === "object" && payload.blocked_by) {
        setError(
          `Impossible : dépend de ${payload.blocked_by.join(", ")}.`,
        );
      } else {
        setError(e.message || "Erreur lors de la mise à jour");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBlockedConfirm = () => {
    if (!blockingReason.trim()) {
      setError("Merci d'indiquer ce qui bloque cette étape.");
      return;
    }
    doAction("bloque", { blocking_reason: blockingReason.trim() });
  };

  const showActions = !readOnly && currentStatus !== STEP_STATUS.DONE;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="step-detail-sheet"
    >
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px] animate-[fade-in_.15s_ease-out]"
      />

      <div
        className="relative w-full sm:max-w-[440px] bg-white rounded-t-3xl sm:rounded-3xl
                   shadow-[0_20px_60px_-20px_rgba(17,17,17,0.35)]
                   max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden
                   animate-[slide-up_.25s_cubic-bezier(0.22,1,0.36,1)]
                   sm:animate-[fade-in_.2s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header compact */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${sm.bg} ${sm.text}`}
                data-testid="step-current-status"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: sm.dot }}
                />
                {sm.label}
              </span>
              {step.visible_client === false ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#111111] text-white px-1.5 py-0.5 text-[9.5px] font-semibold">
                  <EyeOff size={9} strokeWidth={2.5} /> Interne
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F0FFDE] text-[#4a6b0f] px-1.5 py-0.5 text-[9.5px] font-semibold">
                  <Eye size={9} strokeWidth={2.5} /> Client
                </span>
              )}
              {step.mandatory === false && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[#6B7280] px-1.5 py-0.5 text-[9.5px] font-semibold">
                  Facultative
                </span>
              )}
            </div>
            <h3
              className="mt-2 text-[17px] font-semibold text-[#111111] tracking-tight leading-snug"
              data-testid="step-title"
            >
              {step.label || step.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="step-sheet-close"
            aria-label="Fermer"
            className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:text-[#111111] transition-colors -mt-0.5 -mr-0.5"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        {/* Body — la zone défilante ne peut jamais pousser les boutons hors écran */}
        <div className="px-5 pb-3 space-y-3 flex-1 min-h-0 overflow-y-auto">
          {/* Blocage actif */}
          {currentStatus === STEP_STATUS.BLOCKED && step.blocking_reason && (
            <div
              data-testid="step-blocking-reason"
              className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-2.5"
            >
              <div className="flex items-center gap-1 text-[10px] font-semibold text-[#991B1B] uppercase tracking-wider mb-1">
                <AlertTriangle size={10} strokeWidth={2.5} />
                Raison du blocage
              </div>
              <p className="text-[12.5px] text-[#7F1D1D] leading-relaxed">
                {step.blocking_reason}
              </p>
            </div>
          )}

          {/* Dépendances non satisfaites uniquement */}
          {unmetDeps.length > 0 && currentStatus !== STEP_STATUS.DONE && (
            <div
              data-testid="step-unmet-deps"
              className="rounded-2xl border border-[#FEF3C7] bg-[#FFFBEB] px-3 py-2.5"
            >
              <div className="flex items-center gap-1 text-[10px] font-semibold text-[#92400E] uppercase tracking-wider mb-1">
                <Clock size={10} strokeWidth={2.5} />
                Prérequis
              </div>
              <p className="text-[12.5px] text-[#78350F] leading-relaxed">
                Terminer d&apos;abord : {unmetDeps.map((d) => d.label || d.title).join(", ")}.
              </p>
            </div>
          )}

          {/* Note interne */}
          {step.internal_note && (
            <div className="rounded-2xl border border-[#F5E6BD] bg-[#FEF9E4] px-3 py-2.5">
              <div className="flex items-center gap-1 text-[10px] font-semibold text-[#92400E] uppercase tracking-wider mb-1">
                <Lock size={10} strokeWidth={2.25} />
                Note interne
              </div>
              <p className="text-[12.5px] text-[#111111] leading-relaxed">
                {step.internal_note}
              </p>
            </div>
          )}

          {/* Formulaire raison blocage */}
          {showBlockedForm && (
            <div className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-3">
              <label className="block">
                <span className="text-[10.5px] font-semibold text-[#991B1B] uppercase tracking-wider">
                  Qu&apos;est-ce qui bloque ?
                </span>
                <textarea
                  data-testid="blocking-reason-input"
                  value={blockingReason}
                  onChange={(e) => setBlockingReason(e.target.value)}
                  placeholder="Ex : documents client manquants…"
                  rows={2}
                  className="mt-1.5 w-full rounded-xl border border-[#FECACA] bg-white px-3 py-2 text-[13px] outline-none focus:border-[#EF4444] resize-none"
                  autoFocus
                />
              </label>
              <div className="mt-2 flex items-center gap-2">
                <button
                  data-testid="blocking-cancel"
                  onClick={() => { setShowBlockedForm(false); setError(null); }}
                  className="flex-1 h-9 rounded-full bg-white border border-[#EEF2F5] text-[12px] font-medium text-[#111111]"
                >
                  Annuler
                </button>
                <button
                  data-testid="blocking-confirm"
                  onClick={handleBlockedConfirm}
                  disabled={saving}
                  className="flex-1 h-9 rounded-full bg-[#EF4444] text-white text-[12px] font-semibold hover:bg-[#DC2626] disabled:opacity-50"
                >
                  {saving ? "…" : "Marquer bloqué"}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div
              data-testid="step-action-error"
              className="rounded-2xl bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 text-[12.5px] text-[#991B1B]"
            >
              {error}
            </div>
          )}
        </div>

        {/* Actions — TOUJOURS visibles (footer sticky) */}
        {showActions && !showBlockedForm && (
          <div
            data-testid="step-actions"
            className="px-5 py-4 border-t border-[#EEF2F5] grid grid-cols-3 gap-2 shrink-0 bg-white"
          >
            <button
              data-testid="step-action-waiting"
              onClick={() => doAction("en_cours")}
              disabled={saving || currentStatus === STEP_STATUS.IN_PROGRESS}
              className="h-10 rounded-full bg-[#C8FF2F] text-[#111111] text-[12px] font-semibold hover:bg-[#b8ef1d] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
            >
              <Clock size={12} strokeWidth={2.25} />
              En cours
            </button>
            <button
              data-testid="step-action-blocked"
              onClick={() => setShowBlockedForm(true)}
              disabled={saving}
              className="h-10 rounded-full bg-white border border-[#EEF2F5] text-[#991B1B] text-[12px] font-semibold hover:bg-[#FEF2F2] transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
            >
              <AlertTriangle size={12} strokeWidth={2.25} />
              Bloquer
            </button>
            <button
              data-testid="step-action-completed"
              onClick={() => doAction("termine")}
              disabled={saving || unmetDeps.length > 0}
              title={unmetDeps.length > 0 ? "Complétez d'abord les prérequis" : ""}
              className="h-10 rounded-full bg-[#111111] text-white text-[12px] font-semibold hover:bg-black transition-colors disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
            >
              <Check size={12} strokeWidth={2.5} />
              Terminer
            </button>
          </div>
        )}

        {readOnly && (
          <div className="px-5 py-3 border-t border-[#EEF2F5] text-center text-[11.5px] text-[#6B7280] shrink-0 bg-white">
            Dossier terminé — étape en lecture seule.
          </div>
        )}
      </div>
    </div>
  );

  // Sprint V1 · Point 2 fix mobile : rendu via Portal directement sur document.body
  // pour éviter que le `transform` d'un ancêtre (wyvio-fade-in) ne casse le
  // `position: fixed` et déplace la modale hors du viewport sur mobile.
  return createPortal(modal, document.body);
};

export default StepDetailSheet;
